import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLink } from './impersonation';
import { apiRequest } from './api';
import type {
  WorkspaceSummary,
  WorkspaceMessages,
  WorkspaceMessage,
} from '../../shared/src/types/workspace';

export const workspaceRequest = <T,>(token: string, path: string, signal?: AbortSignal) =>
  apiRequest<T>(
    token,
    `/api/workspace${path.replace(/^\/(\?|$)/, '$1')}`,
    { signal },
    'We couldn’t load your conversations. Please try again.'
  );

const date = (value: number | null) =>
  value
    ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : 'Not yet';

export function WorkspaceConversations({
  data,
  token,
  refresh,
  loadMore,
}: {
  data: WorkspaceSummary;
  token: string;
  refresh: number;
  loadMore: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const selected = params.get('thread');
  const conversation = data.conversations.find((item) => item.id === selected);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState<WorkspaceMessages | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [archive, setArchive] = useState('');
  const requestRef = useRef<AbortController | null>(null);
  const load = async (cursor?: string, archivePath?: string, refreshing = false) => {
    if (!selected) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(true);
    setError('');
    try {
      if (archivePath) {
        const result = await workspaceRequest<{ messages: WorkspaceMessage[] }>(
          token,
          `/conversations/${selected}/archive?path=${encodeURIComponent(archivePath)}`,
          controller.signal
        );
        if (!controller.signal.aborted)
          setPage((previous) => ({
            messages: result.messages.sort(
              (a, b) => a.timestamp - b.timestamp || a.id.localeCompare(b.id)
            ),
            nextCursor: null,
            archives: previous?.archives || [],
          }));
      } else if (refreshing && page?.messages.length) {
        const latest = page.messages[page.messages.length - 1];
        let after: string | null = JSON.stringify({ timestamp: latest.timestamp, id: latest.id });
        const incoming: WorkspaceMessage[] = [];
        do {
          const result: WorkspaceMessages = await workspaceRequest(
            token,
            `/conversations/${selected}/messages?after=${encodeURIComponent(after)}`,
            controller.signal
          );
          incoming.push(...result.messages);
          after = result.nextCursor;
        } while (after && !controller.signal.aborted);
        if (!controller.signal.aborted)
          setPage((previous) =>
            previous
              ? {
                  ...previous,
                  messages: [
                    ...new Map(
                      [...previous.messages, ...incoming].map((message) => [message.id, message])
                    ).values(),
                  ].sort((a, b) => a.timestamp - b.timestamp || a.id.localeCompare(b.id)),
                }
              : previous
          );
      } else {
        const result = await workspaceRequest<WorkspaceMessages>(
          token,
          `/conversations/${selected}/messages${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
          controller.signal
        );
        if (!controller.signal.aborted)
          setPage((previous) => ({
            ...result,
            messages: cursor
              ? [...result.messages, ...(previous?.messages || [])].filter(
                  (message, index, all) => all.findIndex((item) => item.id === message.id) === index
                )
              : result.messages,
          }));
      }
    } catch (failure) {
      if (!controller.signal.aborted)
        setError(failure instanceof Error ? failure.message : 'Unable to load messages.');
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };
  useEffect(() => {
    setPage(null);
    setArchive('');
    setError('');
    void load();
    return () => requestRef.current?.abort();
  }, [selected, token]);
  useEffect(() => {
    if (refresh && !archive) void load(undefined, undefined, true);
  }, [refresh]);
  const filtered = data.conversations.filter((item) =>
    `${item.name} ${item.identifier}`.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="workspace-view">
      <header className="workspace-heading workspace-heading-compact">
        <span className="sync-eyebrow">The threads between us</span>
        <h1>Your conversations.</h1>
        <p>Your synced history, ready to revisit. New messages arrive when your desktop syncs.</p>
      </header>
      <div className={`workspace-inbox ${selected ? 'workspace-inbox-selected' : ''}`}>
        <aside className="workspace-conversation-list" aria-label="Conversations">
          <label className="workspace-search">
            <span className="sr-only">Find a conversation</span>
            <input
              type="search"
              placeholder="Find a conversation…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          {filtered.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`workspace-conversation ${selected === item.id ? 'is-selected' : ''}`}
              onClick={() => setParams({ thread: item.id })}
              aria-pressed={selected === item.id}
            >
              <span className="workspace-avatar">
                {item.isGroup ? '♧' : item.name.charAt(0).toUpperCase()}
              </span>
              <span>
                <strong>{item.name}</strong>
                <small>
                  {item.messageCount.toLocaleString()}{' '}
                  {item.messageCount === 1 ? 'message' : 'messages'} · {date(item.lastMessageAt)}
                </small>
              </span>
            </button>
          ))}
          {!filtered.length && (
            <p className="workspace-empty">
              {search
                ? 'No matching conversations.'
                : 'Your conversations will appear after your first sync.'}
            </p>
          )}
          {data.nextOffset !== null && (
            <button type="button" className="sync-text-button workspace-load" onClick={loadMore}>
              Load more conversations
            </button>
          )}
        </aside>
        <section className="workspace-messages" aria-label="Message history">
          {!selected ? (
            <div className="workspace-message-empty">
              <div className="workspace-thread-art" aria-hidden="true">
                <i />
                <i />
                <i />
              </div>
              <h2>A moment worth revisiting.</h2>
              <p>Choose a conversation to read your history.</p>
              {!data.conversations.length && (
                <AppLink className="btn btn-secondary" to="/app/devices">
                  Connect a device
                </AppLink>
              )}
            </div>
          ) : (
            <>
              <header className="workspace-message-heading">
                <button
                  type="button"
                  className="sync-text-button workspace-back"
                  onClick={() => setParams({})}
                  aria-label="Back to conversations"
                >
                  ←
                </button>
                <div>
                  <h2>{conversation?.name || 'Conversation'}</h2>
                  <small>
                    {conversation?.isGroup ? 'Group conversation' : 'Synced conversation'}
                  </small>
                </div>
                {conversation && !conversation.isGroup && (
                  <AppLink
                    className="sync-back-link"
                    to={`/app/devices?recipient=${encodeURIComponent(conversation.identifier)}`}
                  >
                    Write a message ↗
                  </AppLink>
                )}
              </header>
              {page && page.archives.length > 0 && (
                <label className="workspace-archive">
                  History
                  <select
                    value={archive}
                    onChange={(event) => {
                      setArchive(event.target.value);
                      void load(undefined, event.target.value || undefined);
                    }}
                  >
                    <option value="">Recent messages</option>
                    {page.archives.map((item) => (
                      <option key={item.path} value={item.path}>
                        {item.label} archive
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="workspace-transcript" aria-busy={busy}>
                {page?.nextCursor && (
                  <button
                    type="button"
                    className="sync-text-button workspace-load"
                    disabled={busy}
                    onClick={() => void load(page.nextCursor || undefined)}
                  >
                    Load earlier messages
                  </button>
                )}
                {busy && !page && (
                  <p role="status" className="workspace-empty">
                    Loading your conversation…
                  </p>
                )}
                {error && (
                  <div className="sync-error" role="alert">
                    {error}
                    <button
                      className="sync-text-button"
                      onClick={() => void load(undefined, archive || undefined)}
                    >
                      Try again
                    </button>
                  </div>
                )}
                {page?.messages.map((message, index) => (
                  <React.Fragment key={message.id}>
                    {(index === 0 ||
                      new Date(page.messages[index - 1].timestamp).toDateString() !==
                        new Date(message.timestamp).toDateString()) && (
                      <div className="workspace-day">
                        {new Date(message.timestamp).toLocaleDateString(undefined, {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                    )}
                    <article
                      className={`workspace-bubble ${message.outgoing ? 'workspace-bubble-own' : ''}`}
                    >
                      {conversation?.isGroup && !message.outgoing && (
                        <strong>{message.sender}</strong>
                      )}
                      <p>
                        {message.text || (message.hasAttachments ? 'Attachment' : 'Empty message')}
                      </p>
                      {message.hasAttachments && message.text && (
                        <small>Includes an attachment</small>
                      )}
                      <footer>
                        <span>{message.platform}</span>
                        <time dateTime={new Date(message.timestamp).toISOString()}>
                          {new Date(message.timestamp).toLocaleTimeString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </time>
                      </footer>
                    </article>
                  </React.Fragment>
                ))}
                {page && !page.messages.length && (
                  <p className="workspace-empty">
                    No recent messages.{' '}
                    {page.archives.length
                      ? 'Choose an archive above to read earlier history.'
                      : 'Sync your desktop to bring this conversation up to date.'}
                  </p>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
